import React, { useState, useEffect, useMemo } from 'react';
import { ChevronDown, Camera, Image, FileText, Code, File, ExternalLink, Wand2, RefreshCw } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { format } from 'date-fns';
import { AnnotateModal } from './AnnotateModal';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { ClaudeTaskCommand } from '@/types/claudeTask';

const MANAGEMENT_API_BASE = import.meta.env.VITE_PRICINGSAAS_MANAGEMENT_API_URL;
const API_KEY = import.meta.env.VITE_PRICINGSAAS_MANAGEMENT_API_KEY;

interface VersionFile {
  id: string;
  version: string;
  screenshot_url: string | null;
  ocr_url: string | null;
  markdown_url: string | null;
  html_url: string | null;
  page_url: string | null;
  valid: boolean;
  score: number | null;
  banner_free: boolean;
}

interface ScreenshotHistoryProps {
  companySlug: string;
  subslug?: string;
}

export const ScreenshotHistory: React.FC<ScreenshotHistoryProps> = ({
  companySlug,
  subslug,
}) => {
  const [files, setFiles] = useState<VersionFile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isCreatingScrape, setIsCreatingScrape] = useState(false);
  const { toast } = useToast();

  const compositeSlug = subslug ? `${companySlug}.${subslug}` : companySlug;

  // Fetch page with files on mount
  useEffect(() => {
    const fetchPageWithFiles = async () => {
      setLoading(true);
      setError(null);

      try {
        const pageUrl = `${MANAGEMENT_API_BASE}/pages/slug/${compositeSlug}`;
        const response = await fetch(pageUrl, {
          headers: {
            'X-API-Key': API_KEY,
            'Content-Type': 'application/json'
          }
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch page: ${response.status}`);
        }

        const data = await response.json();
        const pageFiles = data.data?.files || [];

        // Sort files by version descending (newest first)
        const sortedFiles = [...pageFiles].sort((a, b) =>
          b.version.localeCompare(a.version)
        );

        setFiles(sortedFiles);
      } catch (err) {
        console.error('[ScreenshotHistory] Error fetching page:', err);
        setError(err instanceof Error ? err.message : 'Failed to load files');
      } finally {
        setLoading(false);
      }
    };

    fetchPageWithFiles();
  }, [compositeSlug]);

  // Format version date (YYYYMMDD -> readable)
  const formatVersionDate = (version: string) => {
    try {
      const year = version.substring(0, 4);
      const month = version.substring(4, 6);
      const day = version.substring(6, 8);
      const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
      return format(date, 'MMM d, yyyy');
    } catch {
      return version;
    }
  };

  // Group files by year
  const filesByYear = useMemo(() => {
    return files.reduce<Record<string, VersionFile[]>>((acc, file) => {
      const year = file.version.substring(0, 4);
      if (!acc[year]) acc[year] = [];
      acc[year].push(file);
      return acc;
    }, {});
  }, [files]);

  const years = Object.keys(filesByYear).sort().reverse();

  // Handle creating scrape task
  const handleRunScrape = async () => {
    setIsCreatingScrape(true);
    try {
      const { data, error } = await supabase
        .from('claude_tasks')
        .insert({
          command: ClaudeTaskCommand.SCRAPE,
          slug: compositeSlug,
          params: { slug: compositeSlug },
          status: 'pending',
          priority: 2,
        })
        .select()
        .single();

      if (error) {
        throw error;
      }

      toast({
        title: 'Scrape task created',
        description: `Scrape task for "${compositeSlug}" has been created with priority 2`,
      });
    } catch (err) {
      console.error('[ScreenshotHistory] Error creating scrape task:', err);
      toast({
        title: 'Error creating scrape task',
        description: err instanceof Error ? err.message : 'Failed to create scrape task',
        variant: 'destructive',
      });
    } finally {
      setIsCreatingScrape(false);
    }
  };

  if (loading) {
    return <ScreenshotHistorySkeleton />;
  }

  if (error) {
    return (
      <div className="text-sm text-red-500 py-2">
        Error loading screenshot history: {error}
      </div>
    );
  }

  if (files.length === 0) {
    return (
      <div className="text-sm text-gray-500 py-2 text-center">
        No screenshots available for this page
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {/* Header */}
      <div className="flex items-center justify-between">
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="flex items-center gap-2 hover:bg-gray-50 rounded px-1 py-1 -mx-1 transition-colors"
        >
          <ChevronDown className={cn(
            "h-4 w-4 text-gray-400 transition-transform",
            !isExpanded && "-rotate-90"
          )} />
          <Camera className="h-4 w-4 text-gray-500" />
          <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
            Screenshot History
          </span>
          <Badge variant="secondary" className="text-xs">
            {files.length} {files.length === 1 ? 'version' : 'versions'}
          </Badge>
        </button>

        <Button
          variant="outline"
          size="sm"
          onClick={handleRunScrape}
          disabled={isCreatingScrape}
          className="h-7 text-xs"
        >
          {isCreatingScrape ? (
            <>
              <RefreshCw className="h-3 w-3 mr-1 animate-spin" />
              Creating...
            </>
          ) : (
            <>
              <RefreshCw className="h-3 w-3 mr-1" />
              Run Scrape
            </>
          )}
        </Button>
      </div>

      {/* Content */}
      {isExpanded && (
        <div className="border rounded-lg divide-y max-h-[400px] overflow-y-auto">
          {years.map((year) => (
            <YearSection
              key={year}
              year={year}
              files={filesByYear[year]}
              formatVersionDate={formatVersionDate}
              slug={compositeSlug}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// Year section component
interface YearSectionProps {
  year: string;
  files: VersionFile[];
  formatVersionDate: (version: string) => string;
  slug: string;
}

const YearSection: React.FC<YearSectionProps> = ({
  year,
  files,
  formatVersionDate,
  slug,
}) => {
  const [isExpanded, setIsExpanded] = useState(year === new Date().getFullYear().toString());

  return (
    <div>
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full px-3 py-2 flex items-center justify-between hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-2">
          <ChevronDown
            className={cn(
              "h-4 w-4 text-gray-400 transition-transform",
              !isExpanded && "-rotate-90"
            )}
          />
          <span className="text-sm font-medium text-gray-700">{year}</span>
          <span className="text-xs text-gray-400">
            ({files.length} {files.length === 1 ? 'snapshot' : 'snapshots'})
          </span>
        </div>
      </button>

      {isExpanded && (
        <div className="px-3 pb-2 space-y-1">
          {files.map((file) => (
            <VersionRow
              key={file.id || file.version}
              file={file}
              formatVersionDate={formatVersionDate}
              slug={slug}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// Individual version row
interface VersionRowProps {
  file: VersionFile;
  formatVersionDate: (version: string) => string;
  slug: string;
}

const VersionRow: React.FC<VersionRowProps> = ({
  file,
  formatVersionDate,
  slug,
}) => {
  const [annotateModalOpen, setAnnotateModalOpen] = useState(false);
  const versionDate = formatVersionDate(file.version);

  return (
    <>
      <div className="flex items-center justify-between py-1.5 px-2 rounded hover:bg-gray-50 transition-colors">
        <span className="text-sm text-gray-600">{versionDate}</span>

        <div className="flex items-center gap-1">
          {file.screenshot_url && (
            <FileLink
              href={file.screenshot_url}
              icon={<Image className="h-3.5 w-3.5" />}
              label="Screenshot"
              color="blue"
            />
          )}
          {file.ocr_url && (
            <FileLink
              href={file.ocr_url}
              icon={<FileText className="h-3.5 w-3.5" />}
              label="OCR"
              color="green"
            />
          )}
          {file.markdown_url && (
            <FileLink
              href={file.markdown_url}
              icon={<File className="h-3.5 w-3.5" />}
              label="MD"
              color="purple"
            />
          )}
          {file.html_url && (
            <FileLink
              href={file.html_url}
              icon={<Code className="h-3.5 w-3.5" />}
              label="HTML"
              color="orange"
            />
          )}
          {file.ocr_url && (
            <button
              onClick={() => setAnnotateModalOpen(true)}
              className="flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium transition-colors text-purple-600 hover:text-purple-800 hover:bg-purple-50"
              title="Annotate screenshot"
            >
              <Wand2 className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">Annotate</span>
            </button>
          )}
          {!file.screenshot_url && !file.ocr_url && !file.markdown_url && !file.html_url && (
            <span className="text-xs text-gray-400">No files</span>
          )}
        </div>
      </div>

      <AnnotateModal
        open={annotateModalOpen}
        onOpenChange={setAnnotateModalOpen}
        slug={slug}
        version={file.version}
        versionDate={versionDate}
      />
    </>
  );
};

// File link button
interface FileLinkProps {
  href: string;
  icon: React.ReactNode;
  label: string;
  color: 'blue' | 'green' | 'purple' | 'orange';
}

const FileLink: React.FC<FileLinkProps> = ({ href, icon, label, color }) => {
  const colorClasses = {
    blue: 'text-blue-600 hover:text-blue-800 hover:bg-blue-50',
    green: 'text-green-600 hover:text-green-800 hover:bg-green-50',
    purple: 'text-purple-600 hover:text-purple-800 hover:bg-purple-50',
    orange: 'text-orange-600 hover:text-orange-800 hover:bg-orange-50',
  };

  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className={cn(
        "flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium transition-colors",
        colorClasses[color]
      )}
      title={`Open ${label}`}
    >
      {icon}
      <span className="hidden sm:inline">{label}</span>
      <ExternalLink className="h-2.5 w-2.5 opacity-50" />
    </a>
  );
};

// Loading skeleton
const ScreenshotHistorySkeleton: React.FC = () => {
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <Skeleton className="h-4 w-4" />
        <Skeleton className="h-4 w-4" />
        <Skeleton className="h-4 w-28" />
        <Skeleton className="h-5 w-16 rounded-full" />
      </div>
    </div>
  );
};

export default ScreenshotHistory;
